import React, { useEffect, useState } from 'react';
import { CreditCard, Calendar, Check, Loader2, Edit2, X, AlertTriangle, XCircle } from 'lucide-react';
import SubscriptionService from '../services/SubscriptionService';
import { PlanService } from '../services/PlanService';
import { ActiveSubscription } from '../types/plan';
import { SessionType } from '../types/appointment';
import { useAuth } from './AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface BillingInfo {
  cardLast4Digits?: string;
  cardExpiry?: string;
  cpfCnpj?: string;
  phone?: string;
  address?: {
    zipCode: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
  };
}

interface UpdateCardFormData {
  cardNumber: string;
  cardName: string;
  cardExpiry: string;
  cardCVV: string;
}

const ActiveSubscriptionsPanel: React.FC = () => {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<ActiveSubscription[]>([]);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUpdateCardModal, setShowUpdateCardModal] = useState(false);
  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  const [updateCardError, setUpdateCardError] = useState<string | null>(null);
  const [updateCardSuccess, setUpdateCardSuccess] = useState(false);
  const [cancelModal, setCancelModal] = useState<{ isOpen: boolean; subscriptionId: string | null; subscriptionName: string }>({
    isOpen: false,
    subscriptionId: null,
    subscriptionName: '',
  });
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cardFormData, setCardFormData] = useState<UpdateCardFormData>({
    cardNumber: '',
    cardName: '',
    cardExpiry: '',
    cardCVV: '',
  });

  useEffect(() => {
    loadSubscriptions();
    loadBillingInfo();
  }, [user]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const subs = await SubscriptionService.getActiveSubscriptions();
      setSubscriptions(subs);
    } catch (err) {
      console.error('Erro ao carregar assinaturas:', err);
      setError('Não foi possível carregar suas assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const loadBillingInfo = async () => {
    if (!user?.uid) return;

    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setBillingInfo(userData?.billingInfo || null);
      }
    } catch (err) {
      console.error('Erro ao carregar informações do cartão:', err);
    }
  };

  const handleUpdateCard = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingCard(true);
    setUpdateCardError(null);
    setUpdateCardSuccess(false);

    try {
      // Validações básicas
      const cardNumberClean = cardFormData.cardNumber.replace(/\s/g, '');
      if (!cardFormData.cardNumber || cardNumberClean.length < 13 || cardNumberClean.length > 19) {
        throw new Error('Número do cartão inválido (13-19 dígitos)');
      }
      if (!cardFormData.cardName || cardFormData.cardName.trim().length < 3) {
        throw new Error('Nome do titular inválido (mínimo 3 caracteres)');
      }
      
      // Validar formato e conteúdo da data de validade
      const expiryMatch = cardFormData.cardExpiry.match(/^(\d{2})\/(\d{2})$/);
      if (!expiryMatch) {
        throw new Error('Data de validade inválida (use MM/AA)');
      }
      
      const month = parseInt(expiryMatch[1], 10);
      const year = parseInt(expiryMatch[2], 10);
      
      if (month < 1 || month > 12) {
        throw new Error('Mês inválido (use 01-12)');
      }
      
      // Validar se a data não está expirada
      const now = new Date();
      const currentYear = now.getFullYear() % 100; // Últimos 2 dígitos do ano
      const currentMonth = now.getMonth() + 1; // Mês atual (1-12)
      
      if (year < currentYear || (year === currentYear && month < currentMonth)) {
        throw new Error('Cartão expirado');
      }
      
      if (!cardFormData.cardCVV || cardFormData.cardCVV.length < 3 || cardFormData.cardCVV.length > 4) {
        throw new Error('CVV inválido (3 ou 4 dígitos)');
      }

      // Chamar Cloud Function para atualizar o cartão
      const updateCardFunction = httpsCallable(functions, 'updateCreditCard');
      const result = await updateCardFunction({
        cardNumber: cardFormData.cardNumber,
        cardName: cardFormData.cardName,
        cardExpiry: cardFormData.cardExpiry,
        cardCVV: cardFormData.cardCVV,
      });

      const response = result.data as { success: boolean; message: string };
      
      if (!response.success) {
        throw new Error(response.message || 'Erro ao atualizar cartão');
      }

      setUpdateCardSuccess(true);
      
      // Limpar formulário
      setCardFormData({
        cardNumber: '',
        cardName: '',
        cardExpiry: '',
        cardCVV: '',
      });

      loadBillingInfo();
      loadSubscriptions();

      // Fechar modal após 3 segundos
      setTimeout(() => {
        setShowUpdateCardModal(false);
        setUpdateCardSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Erro ao atualizar cartão:', err);
      
      let errorMessage = 'Erro ao atualizar cartão. Tente novamente.';
      
      // Tratar erros específicos do Firebase Functions
      if (err?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (err?.code === 'invalid-argument') {
        errorMessage = err?.message || 'Dados do cartão inválidos. Verifique as informações.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = err?.message || 'Você precisa ter uma assinatura ativa para atualizar o cartão.';
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setUpdateCardError(errorMessage);
    } finally {
      setIsUpdatingCard(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted.substring(0, 19); // 16 dígitos + 3 espaços
  };

  const formatExpiry = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return `${cleaned.substring(0, 2)}/${cleaned.substring(2, 4)}`;
    }
    return cleaned;
  };

  const handleCancelSubscription = async () => {
    if (!cancelModal.subscriptionId) return;

    setIsCanceling(true);
    setCancelError(null);

    try {
      const cancelFunction = httpsCallable(functions, 'cancelSubscription');
      const result = await cancelFunction({
        subscriptionId: cancelModal.subscriptionId,
      });

      const response = result.data as { success: boolean; message: string };

      if (!response.success) {
        throw new Error(response.message || 'Erro ao cancelar assinatura');
      }

      // Recarregar assinaturas
      await loadSubscriptions();

      // Fechar modal
      setCancelModal({ isOpen: false, subscriptionId: null, subscriptionName: '' });
    } catch (err: any) {
      console.error('Erro ao cancelar assinatura:', err);

      let errorMessage = 'Erro ao cancelar assinatura. Tente novamente.';

      if (err?.code === 'unauthenticated') {
        errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
      } else if (err?.code === 'permission-denied') {
        errorMessage = 'Você não tem permissão para cancelar esta assinatura.';
      } else if (err?.code === 'failed-precondition') {
        errorMessage = err?.message || 'Esta assinatura não pode ser cancelada.';
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setCancelError(errorMessage);
    } finally {
      setIsCanceling(false);
    }
  };

  const groupedSubscriptions = SubscriptionService.groupBySessionType(subscriptions);
  const hasAnySubscription = subscriptions.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600">Carregando assinaturas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadSubscriptions}
          className="mt-2 text-red-600 hover:text-red-800 font-medium text-sm"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!hasAnySubscription) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          Nenhuma assinatura ativa
        </h3>
        <p className="text-gray-600 text-sm">
          Você não possui assinaturas ativas no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-purple-600" />
          Minhas Assinaturas
        </h2>
        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
          {subscriptions.length} ativa{subscriptions.length !== 1 ? 's' : ''}
        </span>
      </div>


      {/* Informações do Cartão de Crédito */}
      {billingInfo && (billingInfo.cardLast4Digits || billingInfo.cardExpiry) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">Forma de Pagamento</h3>
              <div className="text-sm text-blue-800">
                {billingInfo.cardLast4Digits && (
                  <p className="mb-1">
                    <strong>Cartão:</strong> •••• •••• •••• {billingInfo.cardLast4Digits}
                  </p>
                )}
                {billingInfo.cardExpiry && (
                  <p>
                    <strong>Validade:</strong> {billingInfo.cardExpiry}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowUpdateCardModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Edit2 className="w-4 h-4" />
              Alterar
            </button>
          </div>
        </div>
      )}

      {/* Assinaturas para Conduzir Constelações */}
      {groupedSubscriptions.constellation.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Para Conduzir Sessões
          </h3>
          {groupedSubscriptions.constellation.map((sub) => (
            <SubscriptionCard 
              key={sub.id} 
              subscription={sub} 
              onCancel={(id, name) => setCancelModal({ isOpen: true, subscriptionId: id, subscriptionName: name })}
            />
          ))}
        </div>
      )}

      {/* Assinaturas de Participação */}
      {groupedSubscriptions.participation.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Para Participar de Sessões
          </h3>
          {groupedSubscriptions.participation.map((sub) => (
            <SubscriptionCard 
              key={sub.id} 
              subscription={sub} 
              onCancel={(id, name) => setCancelModal({ isOpen: true, subscriptionId: id, subscriptionName: name })}
            />
          ))}
        </div>
      )}

      {/* Assinaturas de Horas */}
      {groupedSubscriptions.hours.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Créditos em Horas
          </h3>
          {groupedSubscriptions.hours.map((sub) => (
            <SubscriptionCard 
              key={sub.id} 
              subscription={sub} 
              onCancel={(id, name) => setCancelModal({ isOpen: true, subscriptionId: id, subscriptionName: name })}
            />
          ))}
        </div>
      )}

      {/* Outras assinaturas */}
      {groupedSubscriptions.other.length > 0 && (
        <div className="space-y-3">
          {groupedSubscriptions.other.map((sub) => (
            <SubscriptionCard 
              key={sub.id} 
              subscription={sub} 
              onCancel={(id, name) => setCancelModal({ isOpen: true, subscriptionId: id, subscriptionName: name })}
            />
          ))}
        </div>
      )}
      
      {/* Modal de Atualização de Cartão */}
      {showUpdateCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Atualizar Cartão de Crédito
                </h2>
                <button
                  onClick={() => {
                    if (isUpdatingCard) {
                      return; // Não permite fechar durante atualização
                    }
                    setShowUpdateCardModal(false);
                    setUpdateCardError(null);
                    setUpdateCardSuccess(false);
                    setCardFormData({
                      cardNumber: '',
                      cardName: '',
                      cardExpiry: '',
                      cardCVV: '',
                    });
                  }}
                  className={`text-gray-400 hover:text-gray-600 ${
                    isUpdatingCard ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={isUpdatingCard}
                  aria-label="Fechar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {updateCardSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg animate-pulse">
                  <p className="text-green-800 text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Cartão atualizado com sucesso! Fechando...
                  </p>
                </div>
              )}

              {updateCardError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {updateCardError}
                  </p>
                </div>
              )}

              <form onSubmit={handleUpdateCard} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número do Cartão
                  </label>
                  <input
                    type="text"
                    value={cardFormData.cardNumber}
                    onChange={(e) => setCardFormData({ ...cardFormData, cardNumber: formatCardNumber(e.target.value) })}
                    placeholder="1234 5678 9012 3456"
                    maxLength={19}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome no Cartão
                  </label>
                  <input
                    type="text"
                    value={cardFormData.cardName}
                    onChange={(e) => setCardFormData({ ...cardFormData, cardName: e.target.value.toUpperCase() })}
                    placeholder="NOME COMO ESTÁ NO CARTÃO"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validade
                    </label>
                    <input
                      type="text"
                      value={cardFormData.cardExpiry}
                      onChange={(e) => setCardFormData({ ...cardFormData, cardExpiry: formatExpiry(e.target.value) })}
                      placeholder="MM/AA"
                      maxLength={5}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CVV
                    </label>
                    <input
                      type="text"
                      value={cardFormData.cardCVV}
                      onChange={(e) => setCardFormData({ ...cardFormData, cardCVV: e.target.value.replace(/\D/g, '') })}
                      placeholder="123"
                      maxLength={4}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400"
                    />
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-xs flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      O novo cartão será utilizado para todas as suas assinaturas ativas. 
                      As próximas cobranças serão processadas no novo cartão.
                    </span>
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      if (isUpdatingCard) return;
                      setShowUpdateCardModal(false);
                      setUpdateCardError(null);
                      setUpdateCardSuccess(false);
                      setCardFormData({
                        cardNumber: '',
                        cardName: '',
                        cardExpiry: '',
                        cardCVV: '',
                      });
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isUpdatingCard}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isUpdatingCard || updateCardSuccess}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isUpdatingCard ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Atualizando...
                      </>
                    ) : (
                      'Atualizar Cartão'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de Cancelamento */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Cancelar Assinatura
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Tem certeza que deseja cancelar a assinatura <strong>{cancelModal.subscriptionName}</strong>?
                  </p>
                </div>
              </div>

              {cancelError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {cancelError}
                  </p>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Atenção:</strong> Ao cancelar, você não será mais cobrado e perderá o acesso aos benefícios desta assinatura. Esta ação não pode ser desfeita.
                  </span>
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setCancelModal({ isOpen: false, subscriptionId: null, subscriptionName: '' });
                    setCancelError(null);
                  }}
                  disabled={isCanceling}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Manter Assinatura
                </button>
                <button
                  type="button"
                  onClick={handleCancelSubscription}
                  disabled={isCanceling}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isCanceling ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Cancelando...
                    </>
                  ) : (
                    'Sim, Cancelar'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface SubscriptionCardProps {
  subscription: ActiveSubscription;
  onCancel: (subscriptionId: string, subscriptionName: string) => void;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscription, onCancel }) => {
  const formatDate = (date: any): string => {
    if (!date) return 'N/A';
    
    let dateObj: Date;
    
    // Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      dateObj = date.toDate();
    } 
    // Objeto Date
    else if (date instanceof Date) {
      dateObj = date;
    } 
    // String (ex: "2025-12-22" do Asaas)
    else if (typeof date === 'string') {
      dateObj = new Date(date);
    } 
    // Fallback: tentar converter
    else {
      dateObj = new Date(date);
    }
    
    // Validar se a data é válida
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    
    return dateObj.toLocaleDateString('pt-BR');
  };

  const getSubscriptionTypeColor = (sub: ActiveSubscription): string => {
    if (sub.sessionLabel === SessionType.CONSTELACAO) {
      return 'from-purple-500 to-purple-600';
    } else if (sub.sessionLabel === SessionType.PARTICIPACAO) {
      return 'from-green-500 to-green-600';
    } else if (sub.billingType === 'hours') {
      return 'from-blue-500 to-blue-600';
    }
    return 'from-gray-500 to-gray-600';
  };

  return (
    <div className={`bg-gradient-to-r ${getSubscriptionTypeColor(subscription)} rounded-lg shadow-md p-5 text-white`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-5 h-5" />
            <h3 className="font-semibold text-lg">{subscription.planName}</h3>
          </div>
          <p className="text-white/90 text-sm mb-3">
            {subscription.userType === 'therapist' ? 'Plano Terapeuta' : 'Plano Paciente'}
          </p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span className="text-white/90">
                Próximo vencimento: <strong>{formatDate(subscription.nextDueDate)}</strong>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="text-white/90">
                Valor mensal: <strong>{PlanService.formatCurrency(subscription.value)}</strong>
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="bg-white/20 rounded-lg px-3 py-2">
            <p className="text-xs text-white/80">Status</p>
            <p className="font-semibold text-white">Ativa</p>
          </div>
          <button
            onClick={() => onCancel(subscription.id, subscription.planName)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium whitespace-nowrap"
          >
            <XCircle className="w-4 h-4" />
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActiveSubscriptionsPanel;
